import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Lightning, Receipt } from "@phosphor-icons/react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate(next.startsWith("/") ? next : "/");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="login-page">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#002FA7] flex-col justify-between p-12 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-2">
            <img src="/logo.svg" alt="Expenser" className="w-10 h-10 rounded-md flex-shrink-0" data-testid="login-brand-logo" />
            <div>
              <h1 className="text-2xl font-black tracking-tighter leading-none" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                Expenser
              </h1>
              <p className="text-[10px] text-blue-200 uppercase tracking-[0.18em] font-bold mt-1">Expense Management</p>
            </div>
          </div>
          <p className="text-blue-200 text-sm tracking-wide">Real-time expense capture for construction teams</p>
        </div>
        <div className="relative z-10 space-y-8">
          <div className="flex items-start gap-3">
            <Lightning size={20} weight="fill" className="text-yellow-300 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm">Instant Card Swipe Capture</p>
              <p className="text-blue-200 text-xs mt-0.5">Mercury card transactions appear instantly for receipt matching</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Receipt size={20} weight="fill" className="text-emerald-300 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-sm">AI Receipt Scanning</p>
              <p className="text-blue-200 text-xs mt-0.5">Upload receipts and let AI extract vendor, amount and date</p>
            </div>
          </div>
        </div>
        <p className="text-blue-300 text-xs relative z-10">Replaces Sage Expense with QBO + JobTread sync</p>
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.svg" alt="Expenser" className="w-8 h-8 rounded-md flex-shrink-0" />
            <div>
              <h1 className="text-xl font-black tracking-tighter text-zinc-950 leading-none" style={{ fontFamily: 'DM Sans, sans-serif' }}>Expenser</h1>
              <p className="text-[9px] text-zinc-500 uppercase tracking-[0.18em] font-bold mt-0.5">Expense Management</p>
            </div>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900 mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Welcome back
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            Enter your credentials to access your expenses
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-sm mb-4" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1.5 rounded-sm focus:ring-2 focus:ring-[#002FA7]" required />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Password</Label>
                <a href="/forgot-password" className="text-[10px] text-[#002FA7] hover:underline tracking-wide" data-testid="forgot-password-link">
                  Forgot password?
                </a>
              </div>
              <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="mt-1.5 rounded-sm focus:ring-2 focus:ring-[#002FA7]" required />
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm h-10 text-sm font-semibold shadow-sm transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md">
              {loading ? "Please wait..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
